import type { ICellRendererParams } from '@mint-lib/ag-grid/community';
import { Button, TruncateText } from '@mint-lib/tw-components';

import type {
  FileSystemNode,
  FolderNode,
} from '../../../../../types/fileSystem.js';
import { FileIcon } from '../../FileIcon/FileIcon.jsx';
import { FolderNameInput } from '../../FolderSection/FolderNameInput.jsx';

type Props = ICellRendererParams<FileSystemNode> & {
  onOpenFolder: (folder: FolderNode) => void;
  onCommitDraft: (name: string) => boolean;
  onCancelDraft: () => void;
  renamingFolderId: string | null;
  onCommitRename: (id: string, name: string) => boolean;
  onCancelRename: () => void;
  validateName: (name: string, excludeId?: string) => boolean;
};

const NameCell = ({
  node,
  data,
  onOpenFolder,
  onCommitDraft,
  onCancelDraft,
  renamingFolderId,
  onCommitRename,
  onCancelRename,
  validateName,
}: Props) => {
  if (node?.rowPinned) {
    return (
      <div className="flex w-full flex-row items-center gap-2">
        <FileIcon type="folder" />
        <div className="w-full">
          <FolderNameInput
            onCommit={onCommitDraft}
            onCancel={onCancelDraft}
            validateName={validateName}
          />
        </div>
      </div>
    );
  }

  if (!data) return null;

  if (data.kind === 'folder' && data.id === renamingFolderId) {
    const folderId = data.id;

    return (
      <div className="flex w-full flex-row items-center gap-2">
        <FileIcon type="folder" />
        <div className="w-full">
          <FolderNameInput
            initialValue={data.name}
            onCommit={(name) => onCommitRename(folderId, name)}
            onCancel={onCancelRename}
            validateName={(name) => validateName(name, folderId)}
          />
        </div>
      </div>
    );
  }

  if (data.kind === 'folder') {
    return (
      <div className="flex h-full w-full flex-row items-center gap-2">
        <Button
          variant="plain"
          size="small"
          className="w-full justify-start text-text-primary hover:text-text-primary"
          data-dd-action-name="Knowledge base: open folder"
          onDoubleClick={() => onOpenFolder(data)}
        >
          <FileIcon type="folder" />
          <TruncateText>{data.name}</TruncateText>
        </Button>
      </div>
    );
  }

  return <TruncateText>{data.fileName}</TruncateText>;
};

export { NameCell };
